import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as InfoWarningIcon } from '@/assets/icons/info-warning.svg';
import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import { NewDropdown, Option } from '@/components/Forms/Dropdown';
import MultipleFileField from '@/components/Forms/Fields/MultipleFileField';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import InfoTooltip from '@/components/InfoTooltip';
import { Container } from '@/components/Layout';
import { HeadingsRow, PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useUser from '@/hooks/useUser';
import InvoicesBankField from '@/pages/InvoiceList/components/InvoicesBankField';
import {
  BankResource,
  FreelancerDepartmentResource,
  FreelancerInvoiceSubmissionResource,
  InvoiceBuyerResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { currencies } from '@/utils/data';
import { paymentMethodLabels, paymentMethods } from '@/utils/data/payments';
import { FreelancerStatus, PaymentMethod, recipientType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

import VacationHeader from '../RequestVacation/components/VacationHeader';
import InvoiceFieldGroup, {
  AddAnotherButton,
  FieldRow,
} from './components/InvoiceFieldGroup';
import { InvoiceHtmlProps } from './components/InvoiceHtml';
import InvoicePreviewModal from './components/InvoicePreviewModal';
import PaymentTypeToggle from './components/PaymentTypeToggle';
import {
  InvoiceAmountInput,
  InvoiceBonusInput,
  isFilledAmount,
  isFilledBonus,
  prefillAmounts,
  prefillBonuses,
  sumAmounts,
} from './invoiceTotals';

type FormDataT = {
  receiver: string;
  bank_account_id: number | null;
  payment_method: PaymentMethod;
  company_id: number | null;
  currency: 'EUR' | 'USD' | 'GBP';
  amount: string;
  hours: string;
  bonus: string;
  bonus_purpose: string;
  payment_purpose: string;
  invoice_number: string;
  comment: string;
  amounts: InvoiceAmountInput[];
  bonuses: InvoiceBonusInput[];
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const MAX_ADDITIONAL_FILE_SIZE = 5 * 1024 * 1024;
const ADDITIONAL_FILES_ACCEPT =
  '.doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf';

/**
 * Serialises a submission payload into FormData, expanding arrays of objects
 * into Laravel-style bracket keys (e.g. `amounts[0][amount]`). Null/undefined
 * values are skipped.
 */
const appendToFormData = (fd: FormData, payload: Record<string, unknown>) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item && typeof item === 'object') {
          Object.entries(item as Record<string, unknown>).forEach(([k, v]) => {
            if (v !== null && v !== undefined) {
              fd.append(`${key}[${i}][${k}]`, String(v));
            }
          });
        } else {
          fd.append(`${key}[]`, String(item));
        }
      });
    } else {
      fd.append(key, String(value));
    }
  });
};

/**
 * Rounds half up to `decimals` places. Trimming to 12 significant digits
 * first drops float noise, so 7.5 × 33.33 (249.97499…) rounds to 249.98 the
 * way it would on paper rather than down like `toFixed`.
 */
const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(Number((value * factor).toPrecision(12))) / factor;
};

const MAX_HOURS_DECIMALS = 4;

/** Hours × rate, as the amount string the form holds. */
const amountFromHours = (hours: string, rate: number) => {
  const parsed = parseFloat(hours);
  return isNaN(parsed) ? '' : round(parsed * rate, 2).toFixed(2);
};

/**
 * Amount ÷ rate, with the fewest decimals (up to four) for which hours × rate
 * comes back to exactly the amount entered, so the invoice adds up to the cent.
 * If no such value exists within four decimals, the closest one is used.
 */
const hoursFromAmount = (amount: string, rate: number) => {
  const parsed = parseFloat(amount);
  if (isNaN(parsed) || !rate) return '';
  const target = round(parsed, 2);
  for (let d = 0; d <= MAX_HOURS_DECIMALS; d++) {
    const hours = round(parsed / rate, d);
    if (round(hours * rate, 2) === target) return String(hours);
  }
  return String(round(parsed / rate, MAX_HOURS_DECIMALS));
};

const Holder = styled.div`
  margin-top: 80px;
`;

const StyledHeadingsRow = styled(HeadingsRow)`
  align-items: flex-start;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const BottomButtons = styled(Buttons)`
  justify-content: flex-end;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 12px;
`;

const CommentBox = styled(Box)`
  margin-bottom: 24px;
`;

const Comment = styled(Text)`
  border-radius: 4px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  max-width: 480px;
`;

const CommentLabel = styled(Text)`
  margin-bottom: 12px;
`;

const Note = styled.div`
  border-radius: 4px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  gap: 12px;
  margin-bottom: 24px;
`;

const BoxInner = styled.div`
  max-width: 480px;
`;

const InputHolder = styled.div`
  position: relative;
  width: 100%;
`;

const CurrencySymbol = styled(Text)`
  position: absolute;
  left: 16px;
  top: 12px;
`;

const RateNote = styled(Text)`
  margin-top: 8px;
`;

const AmountInput = styled(Input)`
  padding-left: 34px;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const GenerateInvoice = () => {
  const alerts = useAlerts();
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const prefill = location.state?.submission as
    | FreelancerInvoiceSubmissionResource
    | undefined;

  useEffect(() => {
    if (user && !user.freelancer?.id) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const isVatPayer = !!user?.freelancer?.vat_number;
  const showVatPayerFields = user?.freelancer?.country === 'LT' && isVatPayer;

  const { data: departments = [], isLoading: isDepartmentsLoading } = useQuery<
    FreelancerDepartmentResource[]
  >(
    ['freelancer_departments', { id: user?.freelancer?.id }],
    () =>
      api.get<never, FreelancerDepartmentResource[]>(
        `/v1/payments/freelancers/${user?.freelancer?.id}/departments`,
        { params: { 'filter[status]': FreelancerStatus.EMPLOYED } },
      ),
    { enabled: !!user?.freelancer?.id, staleTime: 60 * 1000 },
  );

  const { data: freelancerBanks = [] } = useQuery<BankResource[]>(
    ['freelancer_banks', { receiver: user?.freelancer?.name }],
    () =>
      api.get<never, BankResource[]>(
        `/v1/payments/members/banks/${user?.freelancer?.name}`,
      ),
    { enabled: !!user?.freelancer?.name, staleTime: 60 * 1000 },
  );

  const [selectedBank, setSelectedBank] = useState<BankResource | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);

  // Each department detail can be billed to several companies, and a company
  // can appear under several details. The invoice is issued to the company and
  // the API resolves which detail it belongs to.
  const companies = useMemo(() => {
    const byId = new Map<number, InvoiceBuyerResource>();
    departments.forEach((d) =>
      d.companies.forEach((c) => {
        if (!byId.has(c.id)) byId.set(c.id, c);
      }),
    );
    return Array.from(byId.values());
  }, [departments]);

  const companyOptions: Option<number>[] = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.name })),
    [companies],
  );

  const {
    register,
    control,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      currency: prefill?.currency ?? 'EUR',
      bank_account_id: prefill?.bankAccount?.id ?? null,
      payment_method: prefill?.paymentMethod ?? PaymentMethod.Bank,
      company_id: prefill?.companyId ?? null,
      receiver: user?.freelancer?.name ?? '',
      amount: prefill ? String(prefill.amount) : '',
      hours: prefill?.hours ? String(prefill.hours) : '',
      bonus: prefill?.bonus ? String(prefill.bonus) : '',
      bonus_purpose: '',
      payment_purpose: prefill?.paymentPurpose ?? '',
      invoice_number: prefill?.invoiceNumber ?? '',
      comment: prefill?.comment ?? '',
      amounts: prefillAmounts(prefill),
      bonuses: prefillBonuses(prefill),
    },
  });

  const amountsArray = useFieldArray({ control, name: 'amounts' });
  const bonusesArray = useFieldArray({ control, name: 'bonuses' });

  // A lone group needs neither a number nor a way to remove itself.
  const hasMultipleAmounts = amountsArray.fields.length > 1;
  const hasMultipleBonuses = bonusesArray.fields.length > 1;

  const freelancerName = user?.freelancer?.name;

  useEffect(() => {
    if (freelancerName) {
      setValue('receiver', freelancerName);
    }
  }, [freelancerName, setValue]);

  useEffect(() => {
    if (companyOptions.length === 1) {
      setValue('company_id', companyOptions[0].value);
    }
  }, [companyOptions, setValue]);

  const currentCurrency = useWatch({ control, name: 'currency' });
  const currencySymbol = CURRENCY_SYMBOLS[currentCurrency] ?? '€';

  const paypalEmail = user?.freelancer?.paypal_email;
  const selectedMethod = useWatch({ control, name: 'payment_method' });

  // A freelancer can only pick a method they have details for. Payoneer is no
  // longer offered, so a stored Payoneer email is not an option here.
  const availableMethods = [
    ...(freelancerBanks.length > 0 ? [PaymentMethod.Bank] : []),
    ...(paypalEmail ? [PaymentMethod.Paypal] : []),
  ];

  // Only offer the switch when there is more than one option.
  const showPaymentToggle = availableMethods.length > 1;
  // With a single option the freelancer is pinned to it, whatever the form
  // holds.
  const paymentMethod = availableMethods.includes(selectedMethod)
    ? selectedMethod
    : availableMethods[0] ?? PaymentMethod.Bank;
  const isWallet = paymentMethod !== PaymentMethod.Bank;

  const handlePaymentTypeChange = (method: PaymentMethod) => {
    setValue('payment_method', method);
    if (method !== PaymentMethod.Bank) {
      setValue('bank_account_id', null);
      setSelectedBank(null);
    }
  };

  const selectedCompanyId = useWatch({ control, name: 'company_id' });
  // Role and rate still live on the department detail; take the first one
  // under the selected company.
  const selectedDepartment = departments.find((d) =>
    d.companies.some((c) => c.id === selectedCompanyId),
  );
  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
  const ACTOR_ROLES = ['actor', 'actor pet', 'child actor'];
  const isActorRole = ACTOR_ROLES.includes(
    selectedDepartment?.role?.toLowerCase() ?? '',
  );
  const isMultiLanguage = !!selectedDepartment?.financeAccountNumber?.name
    ?.toLowerCase()
    .includes('multi-language');
  // Actors and multi-language departments invoice several amounts/bonuses.
  const hasMultipleEntries = isActorRole || isMultiLanguage;
  const hourlyRate = selectedDepartment?.hourlyRate;

  const currentHours = useWatch({ control, name: 'hours' });
  const currentAmount = useWatch({ control, name: 'amount' });
  const currentBonus = useWatch({ control, name: 'bonus' });
  const currentBonusPurpose = useWatch({ control, name: 'bonus_purpose' });
  const currentPaymentPurpose = useWatch({ control, name: 'payment_purpose' });
  const currentInvoiceNumber = useWatch({ control, name: 'invoice_number' });
  const rawAmounts = useWatch({ control, name: 'amounts' });
  const watchedAmounts = useMemo(() => rawAmounts ?? [], [rawAmounts]);
  const watchedBonuses = useWatch({ control, name: 'bonuses' }) ?? [];

  useEffect(() => {
    if (hourlyRate) {
      const currencyMap: Record<string, FormDataT['currency']> = {
        euro: 'EUR',
        dollar: 'USD',
        pound: 'GBP',
      };
      const mapped = currencyMap[hourlyRate.currency.toLowerCase()];
      if (mapped) setValue('currency', mapped);
    }
  }, [hourlyRate, setValue]);

  // With an hourly rate, freelancers can fill in either hours or amount and the
  // other is calculated. Whichever they typed last is the one kept when the
  // rate changes (e.g. switching company). A stored submission without hours
  // keeps its amount rather than having it wiped.
  const lastRateInput = useRef<'hours' | 'amount'>(
    prefill && !prefill.hours ? 'amount' : 'hours',
  );
  // The same, per amount row, keyed by field id so removing a row doesn't
  // shift the others' choice. Rows without an entry fall back to hours, or to
  // amount when prefilled without hours.
  const lastRowRateInput = useRef(new Map<string, 'hours' | 'amount'>());
  const rowRateInput = (fieldId?: string, li?: InvoiceAmountInput) =>
    (fieldId && lastRowRateInput.current.get(fieldId)) ||
    (prefill && li?.amount && !li.hours ? 'amount' : 'hours');

  useEffect(() => {
    if (hasMultipleEntries || !hourlyRate) return;
    if (lastRateInput.current === 'amount') {
      setValue(
        'hours',
        hoursFromAmount(getValues('amount'), Number(hourlyRate.amount)),
      );
    } else {
      setValue(
        'amount',
        amountFromHours(getValues('hours'), Number(hourlyRate.amount)),
      );
    }
  }, [hourlyRate, hasMultipleEntries, getValues, setValue]);

  useEffect(() => {
    if (!hasMultipleEntries || !hourlyRate) return;
    const rate = Number(hourlyRate.amount);
    getValues('amounts').forEach((li, i) => {
      const fieldId = amountsArray.fields[i]?.id;
      if (rowRateInput(fieldId, li) === 'amount') {
        setValue(`amounts.${i}.hours`, hoursFromAmount(li.amount, rate));
      } else {
        setValue(`amounts.${i}.amount`, amountFromHours(li.hours, rate));
      }
    });
    // Only re-derive when the rate or form mode changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourlyRate, hasMultipleEntries, getValues, setValue]);

  const isLT = user?.freelancer?.country?.toUpperCase() === 'LT';
  const actorSubtotal = sumAmounts(watchedAmounts) + sumAmounts(watchedBonuses);
  const nonActorSubtotal =
    (parseFloat(currentAmount) || 0) + (parseFloat(currentBonus) || 0);
  const vat =
    isLT && isVatPayer
      ? (hasMultipleEntries ? actorSubtotal : nonActorSubtotal) * 0.21
      : 0;

  const hasValidAmount = watchedAmounts.some(
    (li) => li.amount && li.payment_purpose,
  );
  const isPreviewDisabled =
    (!isWallet && !selectedBank) ||
    (companyOptions.length > 0 && !selectedCompanyId) ||
    (hasMultipleEntries
      ? !hasValidAmount
      : !currentAmount || !currentPaymentPurpose);

  const invoicePreviewProps: InvoiceHtmlProps = {
    currency: currentCurrency,
    amount: parseFloat(currentAmount) || 0,
    hours:
      hourlyRate && currentHours
        ? parseFloat(currentHours) || undefined
        : undefined,
    bonus: currentBonus ? parseFloat(currentBonus) || undefined : undefined,
    bonusPurpose: currentBonusPurpose || undefined,
    paymentPurpose: currentPaymentPurpose,
    invoiceNumber: currentInvoiceNumber || undefined,
    freelancerName: user?.freelancer?.name ?? '',
    freelancerCountry: user?.freelancer?.country ?? '',
    freelancerVatNumber: user?.freelancer?.vat_number,
    freelancerAddress: isVatPayer
      ? user?.freelancer?.company_address ?? user?.freelancer?.address
      : user?.freelancer?.address,
    freelancerPostCode:
      isVatPayer && user?.freelancer?.company_address
        ? undefined
        : user?.freelancer?.post_code,
    freelancerActivityCode: user?.freelancer?.activity_code,
    freelancerPersonalCode: user?.freelancer?.personal_code,
    bankIban: selectedBank?.iban ?? '',
    bankName: selectedBank?.bank ?? '',
    bankSwift: selectedBank?.swift_code,
    trueReceiver: selectedBank?.true_receiver,
    isCompanyReceiver: selectedBank?.recipient_type === recipientType.COMPANY,
    paymentMethod,
    walletEmail: paypalEmail ?? undefined,
    vat,
    date: new Date().toISOString().split('T')[0],
    amounts: hasMultipleEntries
      ? watchedAmounts.filter(isFilledAmount).map((li) => ({
          amount: parseFloat(li.amount) || 0,
          hours: li.hours ? parseFloat(li.hours) || undefined : undefined,
          paymentPurpose: li.payment_purpose,
        }))
      : undefined,
    bonuses: hasMultipleEntries
      ? watchedBonuses.filter(isFilledBonus).map((b) => ({
          amount: parseFloat(b.amount) || 0,
          purpose: b.purpose || undefined,
        }))
      : undefined,
    buyer: selectedCompany,
  };

  const submitInvoice = handleSubmit(async (data) => {
    const {
      company_id,
      amounts,
      bonuses,
      amount,
      hours,
      bonus,
      bonus_purpose,
      payment_purpose,
      // Sent below as the clamped `paymentMethod`, not the raw form value.
      payment_method: _paymentMethod,
      ...common
    } = data;
    const roleFields = hasMultipleEntries
      ? {
          amounts: amounts.filter(isFilledAmount),
          bonuses: bonuses.filter(isFilledBonus),
        }
      : {
          amount,
          // Hours are only asked for when there's a rate to multiply them by.
          hours: hourlyRate ? hours : undefined,
          bonus,
          bonus_purpose,
          payment_purpose,
        };
    const withCompany =
      companyOptions.length === 0
        ? { ...common, ...roleFields }
        : { ...common, company_id, ...roleFields };
    const payload = {
      ...withCompany,
      payment_method: paymentMethod,
      is_actor: hasMultipleEntries ? 1 : 0,
    };

    const formData = new FormData();
    appendToFormData(formData, payload as Record<string, unknown>);
    additionalFiles.forEach((file) =>
      formData.append('additional_files[]', file),
    );

    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
    };
    try {
      if (prefill) {
        // multipart can't be sent as PUT; spoof the method for Laravel.
        formData.append('_method', 'PUT');
        await api.post(
          `/v1/payments/freelancer-invoice-submissions/${prefill.id}`,
          formData,
          config,
        );
      } else {
        await api.post(
          '/v1/payments/freelancer-invoice-submissions',
          formData,
          config,
        );
      }
      await queryClient.invalidateQueries(['freelancer_invoice_submissions'], {
        exact: false,
      });
      alerts.success('Invoice has been submitted successfully');
      navigate('/general/freelancers/invoices');
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
    }
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    submitInvoice(e);
  };

  return (
    <>
      <VacationHeader />
      <Container>
        <Holder>
          <form onSubmit={onSubmit} autoComplete="off">
            <StyledHeadingsRow>
              <div>
                <Text $type="caption" $color="gray" $isUpperCase>
                  Freelancer
                </Text>
                <PageHeading>
                  {prefill ? 'Edit' : 'Create'} Invoice{' '}
                  <Text as="span" $color="gray" $type="heading1">
                    ·{' '}
                    {isActorRole
                      ? 'Actor'
                      : isMultiLanguage
                        ? 'Multi-language'
                        : 'Regular'}
                  </Text>
                </PageHeading>
              </div>
              <Buttons>
                <Button
                  color="secondary"
                  as={Link}
                  to="/general/freelancers/invoices"
                  style={{ background: 'transparent' }}
                >
                  Cancel
                </Button>
                <Button
                  $type="primary"
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                  disabled={isPreviewDisabled}
                >
                  Preview
                </Button>
                <Button
                  $type="primary"
                  type="submit"
                  disabled={isSubmitting || isDepartmentsLoading}
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              </Buttons>
            </StyledHeadingsRow>
            {prefill && (
              <CommentBox>
                <CommentLabel $type="smallBold">Comment</CommentLabel>
                <Comment $type="smallBold">{prefill.teamLeadComment}</Comment>
              </CommentBox>
            )}
            <Box>
              <BoxInner>
                <FormLayout>
                  <FormField<FormDataT>
                    control={control}
                    name="receiver"
                    label="Receiver Name"
                    disabled
                  />
                  {showPaymentToggle && (
                    <Larger>
                      <PaymentTypeToggle
                        value={paymentMethod}
                        onChange={handlePaymentTypeChange}
                        disabledMethods={paymentMethods.filter(
                          (method) => !availableMethods.includes(method),
                        )}
                      />
                    </Larger>
                  )}
                  {isWallet ? (
                    <Larger>
                      <Label htmlFor="wallet_email">
                        {paymentMethodLabels[paymentMethod]} Email
                      </Label>
                      <Input
                        id="wallet_email"
                        value={paypalEmail ?? ''}
                        disabled
                        readOnly
                      />
                    </Larger>
                  ) : (
                    <Larger>
                      <InvoicesBankField
                        name="bank_account_id"
                        control={control}
                        setValue={setValue}
                        initialReceiver={user?.freelancer?.name ?? ''}
                        showNewBankDetails={false}
                        onBankSelect={setSelectedBank}
                        disableWhenSingleBank
                        autoSelectSingleBank
                      />
                      {!!errors.bank_account_id && (
                        <ValidationMessage>
                          {errors.bank_account_id.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                  )}
                  <Larger>
                    <Label>Company</Label>
                    <NewDropdown<number>
                      options={companyOptions}
                      control={control}
                      name="company_id"
                      placeholder={
                        isDepartmentsLoading ? 'Loading...' : 'Select'
                      }
                      disabled={
                        isDepartmentsLoading || companyOptions.length === 1
                      }
                    />
                    {!!errors.company_id && (
                      <ValidationMessage>
                        {errors.company_id.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                  {showVatPayerFields && (
                    <Larger>
                      <FormField<FormDataT>
                        control={control}
                        name="invoice_number"
                        label="Invoice Number"
                        placeholder="Add invoice number"
                        optional
                      />
                    </Larger>
                  )}
                  <Larger>
                    <Label>Currency</Label>
                    <NewDropdown
                      options={currencies}
                      control={control}
                      name="currency"
                      disabled={!!hourlyRate}
                    />
                    {!!errors.currency && (
                      <ValidationMessage>
                        {errors.currency.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                  {hasMultipleEntries ? (
                    <>
                      <Larger>
                        {amountsArray.fields.map((field, index) => (
                          <InvoiceFieldGroup
                            key={field.id}
                            label="Amount"
                            index={index}
                            showIndex={hasMultipleAmounts}
                            onRemove={
                              hasMultipleAmounts
                                ? () => amountsArray.remove(index)
                                : undefined
                            }
                          >
                            <FieldRow>
                              <div>
                                <Input
                                  placeholder="Add hours"
                                  {...register(`amounts.${index}.hours`, {
                                    onChange: (e) => {
                                      if (!hourlyRate) return;
                                      lastRowRateInput.current.set(
                                        field.id,
                                        'hours',
                                      );
                                      setValue(
                                        `amounts.${index}.amount`,
                                        amountFromHours(
                                          e.target.value,
                                          Number(hourlyRate.amount),
                                        ),
                                      );
                                    },
                                  })}
                                  $hasError={!!errors.amounts?.[index]?.hours}
                                />
                                {!!errors.amounts?.[index]?.hours && (
                                  <ValidationMessage>
                                    {errors.amounts[index]!.hours!.message}
                                  </ValidationMessage>
                                )}
                              </div>
                              <div>
                                <InputHolder>
                                  <AmountInput
                                    placeholder="0"
                                    {...register(`amounts.${index}.amount`, {
                                      onChange: (e) => {
                                        if (!hourlyRate) return;
                                        lastRowRateInput.current.set(
                                          field.id,
                                          'amount',
                                        );
                                        setValue(
                                          `amounts.${index}.hours`,
                                          hoursFromAmount(
                                            e.target.value,
                                            Number(hourlyRate.amount),
                                          ),
                                        );
                                      },
                                    })}
                                    $hasError={
                                      !!errors.amounts?.[index]?.amount
                                    }
                                  />
                                  <CurrencySymbol $type="captionSemibold">
                                    {currencySymbol}
                                  </CurrencySymbol>
                                </InputHolder>
                                {!!errors.amounts?.[index]?.amount && (
                                  <ValidationMessage>
                                    {errors.amounts[index]!.amount!.message}
                                  </ValidationMessage>
                                )}
                              </div>
                            </FieldRow>
                            <div>
                              <Label
                                htmlFor={`amounts.${index}.payment_purpose`}
                              >
                                Purpose of Payment
                              </Label>
                              <Input
                                id={`amounts.${index}.payment_purpose`}
                                placeholder="e.g. Weekend overtime"
                                {...register(
                                  `amounts.${index}.payment_purpose`,
                                )}
                                $hasError={
                                  !!errors.amounts?.[index]?.payment_purpose
                                }
                              />
                              {!!errors.amounts?.[index]?.payment_purpose && (
                                <ValidationMessage>
                                  {
                                    errors.amounts[index]!.payment_purpose!
                                      .message
                                  }
                                </ValidationMessage>
                              )}
                            </div>
                          </InvoiceFieldGroup>
                        ))}
                        <AddAnotherButton
                          onClick={() =>
                            amountsArray.append({
                              hours: '',
                              amount: '',
                              payment_purpose: '',
                            })
                          }
                        >
                          Add another amount
                        </AddAnotherButton>
                        {!!hourlyRate && (
                          <RateNote $type="caption" $color="gray">
                            Your hourly rate: {currencySymbol}
                            {Number(hourlyRate.amount).toFixed(2)} / h. Enter
                            hours or amount and the other is calculated.
                          </RateNote>
                        )}
                        {!!errors.amounts?.message && (
                          <ValidationMessage>
                            {errors.amounts.message}
                          </ValidationMessage>
                        )}
                      </Larger>
                      <Larger>
                        {bonusesArray.fields.map((field, index) => (
                          <InvoiceFieldGroup
                            key={field.id}
                            label={
                              <>
                                Bonus{' '}
                                <Text as="span" $type="smallBold" $color="gray">
                                  (Optional)
                                </Text>
                              </>
                            }
                            index={index}
                            showIndex={hasMultipleBonuses}
                            onRemove={
                              hasMultipleBonuses
                                ? () => bonusesArray.remove(index)
                                : undefined
                            }
                          >
                            <div>
                              <InputHolder>
                                <AmountInput
                                  placeholder="Add amount"
                                  {...register(`bonuses.${index}.amount`)}
                                  $hasError={!!errors.bonuses?.[index]?.amount}
                                />
                                <CurrencySymbol $type="captionSemibold">
                                  {currencySymbol}
                                </CurrencySymbol>
                              </InputHolder>
                              {!!errors.bonuses?.[index]?.amount && (
                                <ValidationMessage>
                                  {errors.bonuses[index]!.amount!.message}
                                </ValidationMessage>
                              )}
                            </div>
                            <div>
                              <Label htmlFor={`bonuses.${index}.purpose`}>
                                Purpose{' '}
                                <Text as="span" $type="smallBold" $color="gray">
                                  (Optional)
                                </Text>
                              </Label>
                              <Input
                                id={`bonuses.${index}.purpose`}
                                placeholder="e.g. Holiday bonus"
                                {...register(`bonuses.${index}.purpose`)}
                                $hasError={!!errors.bonuses?.[index]?.purpose}
                              />
                              {!!errors.bonuses?.[index]?.purpose && (
                                <ValidationMessage>
                                  {errors.bonuses[index]!.purpose!.message}
                                </ValidationMessage>
                              )}
                            </div>
                          </InvoiceFieldGroup>
                        ))}
                        <AddAnotherButton
                          onClick={() =>
                            bonusesArray.append({ amount: '', purpose: '' })
                          }
                        >
                          Add another bonus
                        </AddAnotherButton>
                        {!!errors.bonuses?.message && (
                          <ValidationMessage>
                            {errors.bonuses.message}
                          </ValidationMessage>
                        )}
                      </Larger>
                    </>
                  ) : (
                    <>
                      <Larger>
                        <FieldRow>
                          {!!hourlyRate && (
                            <div>
                              <Label htmlFor="hours">Hours</Label>
                              <Input
                                id="hours"
                                placeholder="Add hours"
                                {...register('hours', {
                                  onChange: (e) => {
                                    if (!hourlyRate) return;
                                    lastRateInput.current = 'hours';
                                    setValue(
                                      'amount',
                                      amountFromHours(
                                        e.target.value,
                                        Number(hourlyRate.amount),
                                      ),
                                    );
                                  },
                                })}
                                $hasError={!!errors.hours}
                              />
                              {!!errors.hours && (
                                <ValidationMessage>
                                  {errors.hours.message}
                                </ValidationMessage>
                              )}
                            </div>
                          )}
                          <div>
                            <Label htmlFor="amount">Amount</Label>
                            <InputHolder>
                              <AmountInput
                                id="amount"
                                placeholder="Add amount"
                                {...register('amount', {
                                  onChange: (e) => {
                                    if (!hourlyRate) return;
                                    lastRateInput.current = 'amount';
                                    setValue(
                                      'hours',
                                      hoursFromAmount(
                                        e.target.value,
                                        Number(hourlyRate.amount),
                                      ),
                                    );
                                  },
                                })}
                                $hasError={!!errors.amount}
                              />
                              <CurrencySymbol $type="captionSemibold">
                                {currencySymbol}
                              </CurrencySymbol>
                            </InputHolder>
                            {!!errors.amount && (
                              <ValidationMessage>
                                {errors.amount.message}
                              </ValidationMessage>
                            )}
                          </div>
                        </FieldRow>
                        {!!hourlyRate && (
                          <RateNote $type="caption" $color="gray">
                            Your hourly rate: {currencySymbol}
                            {Number(hourlyRate.amount).toFixed(2)} / h. Enter
                            hours or amount and the other is calculated.
                          </RateNote>
                        )}
                      </Larger>
                      <Larger>
                        <Label htmlFor="payment_purpose">
                          Purpose of Payment
                          <InfoTooltip
                            title="Purpose of Payment"
                            content="Description of services provided."
                            position="bottom-center"
                          />
                        </Label>
                        <Input
                          id="payment_purpose"
                          placeholder="Add purpose"
                          {...register('payment_purpose')}
                          $hasError={!!errors.payment_purpose}
                        />
                        {!!errors.payment_purpose && (
                          <ValidationMessage>
                            {errors.payment_purpose.message}
                          </ValidationMessage>
                        )}
                      </Larger>
                    </>
                  )}
                  {!hasMultipleEntries && (
                    <>
                      <Larger>
                        <Label htmlFor="bonus">
                          Bonus{' '}
                          <Text as="span" $type="smallBold" $color="gray">
                            (Optional)
                          </Text>
                        </Label>
                        <InputHolder>
                          <AmountInput
                            id="bonus"
                            placeholder="Add bonus"
                            {...register('bonus')}
                            $hasError={!!errors.bonus}
                          />
                          <CurrencySymbol $type="captionSemibold">
                            {currencySymbol}
                          </CurrencySymbol>
                        </InputHolder>
                        {!!errors.bonus && (
                          <ValidationMessage>
                            {errors.bonus.message}
                          </ValidationMessage>
                        )}
                      </Larger>
                      {showVatPayerFields && (
                        <Larger>
                          <FormField<FormDataT>
                            control={control}
                            name="bonus_purpose"
                            label="Bonus Purpose"
                            placeholder="Add bonus purpose"
                            optional
                          />
                        </Larger>
                      )}
                    </>
                  )}
                  <FormField<FormDataT>
                    control={control}
                    name="comment"
                    label="Comment"
                    type="textarea"
                    placeholder="Add comments"
                    optional
                  />
                  <Larger>
                    <MultipleFileField
                      label={
                        <>
                          Additional Files{' '}
                          <Text as="span" $type="smallBold" $color="gray">
                            (Optional)
                          </Text>
                        </>
                      }
                      value={additionalFiles}
                      onChange={setAdditionalFiles}
                      accept={ADDITIONAL_FILES_ACCEPT}
                      maxSize={MAX_ADDITIONAL_FILE_SIZE}
                      notes="PDF, doc, docx, xls, xlsx / Size: Up to 5MB each"
                    />
                  </Larger>
                </FormLayout>
              </BoxInner>
            </Box>
            <Note>
              <InfoWarningIcon />
              <Text $type="smallBold">
                Once submitted, your team lead will need to approve your invoice
              </Text>
            </Note>
            <BottomButtons>
              <Button
                color="secondary"
                as={Link}
                to="/general/freelancers/invoices"
                style={{ background: 'transparent' }}
              >
                Cancel
              </Button>
              <Button
                $type="primary"
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                disabled={isPreviewDisabled}
              >
                Preview
              </Button>
              <Button
                $type="primary"
                type="submit"
                disabled={isSubmitting || isDepartmentsLoading}
                loading={isSubmitting}
              >
                Submit
              </Button>
            </BottomButtons>
          </form>
        </Holder>
      </Container>
      <AnimatePresence>
        {isPreviewOpen && (
          <InvoicePreviewModal
            onClose={() => setIsPreviewOpen(false)}
            onSubmit={() => {
              clearErrors();
              setIsPreviewOpen(false);
              submitInvoice();
            }}
            invoiceProps={invoicePreviewProps}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GenerateInvoice;
